import { useMemo } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock } from 'lucide-react';
import type { KudozPromotion, UserCoupon } from './types';

function formatExpiry(dateStr?: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const diffDays = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'Expired';
  if (diffDays === 0) return 'Expires today';
  if (diffDays === 1) return 'Expires tomorrow';
  if (diffDays <= 7) return `${diffDays}d left`;
  return `Until ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

interface DealItem {
  id: string;
  title: string;
  subtitle?: string | null;
  badge: string;
  expiry?: string | null;
}

export default function DealsWidget() {
  const [, navigate] = useLocation();

  const { data: promotions, isLoading: promoLoading } = useQuery<KudozPromotion[]>({
    queryKey: ['/api/kudoz/promotions'],
    staleTime: 5 * 60_000,
  });

  const { data: coupons, isLoading: couponsLoading } = useQuery<UserCoupon[]>({
    queryKey: ['/api/coupons/active'],
    staleTime: 5 * 60_000,
  });

  const isLoading = promoLoading || couponsLoading;

  const allDeals = useMemo((): DealItem[] => {
    const promoItems: DealItem[] = (promotions ?? []).map((p) => ({
      id: `promo-${p.id}`,
      title: p.title ?? p.name ?? 'Promotion',
      subtitle: p.description,
      badge: p.kudozMultiplier ? `${p.kudozMultiplier}× Kudoz` : 'Promo',
      expiry: formatExpiry(p.expiresAt ?? p.endDate),
    }));
    const couponItems: DealItem[] = (coupons ?? []).map((c) => ({
      id: `coupon-${c.id}`,
      title: c.description ?? c.name ?? 'Coupon',
      subtitle: c.code ? `Code: ${c.code}` : c.restaurantName,
      badge: c.discountType === 'percent' ? `${c.discountValue}% off` : `$${c.discountValue} off`,
      expiry: formatExpiry(c.expiresAt ?? c.validUntil),
    }));
    return [...promoItems, ...couponItems].slice(0, 6);
  }, [promotions, coupons]);

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-1 px-4" style={{ scrollbarWidth: 'none' }}>
        {[1, 2].map(i => <Skeleton key={i} className="flex-shrink-0 w-[180px] h-[88px] rounded-lg" />)}
      </div>
    );
  }

  if (allDeals.length === 0) {
    return (
      <div className="px-4 py-2 text-center text-sm text-muted-foreground">
        No active deals right now. Check back soon!
      </div>
    );
  }

  return (
    <div className="flex gap-3 overflow-x-auto pb-1 px-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      {allDeals.map((d) => (
        <Card
          key={d.id}
          className="flex-shrink-0 w-[180px] hover-elevate active-elevate-2 cursor-pointer"
          onClick={() => navigate('/restaurants')}
        >
          <CardContent className="p-3 space-y-1.5">
            <Badge variant="secondary" className="text-xs">{d.badge}</Badge>
            <div className="text-sm font-semibold truncate">{d.title}</div>
            {d.subtitle && <div className="text-xs text-muted-foreground truncate">{d.subtitle}</div>}
            {d.expiry && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3 flex-shrink-0" />
                {d.expiry}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
